import type { Component } from 'vue';
import type { RouteRecordRaw, NavigationGuardNext } from 'vue-router';
import ActivationOrPasswordRecoveryMode from '@/areas/login/views/ActivationOrPasswordRecovery.types';
import { RouteName } from '@/router/route-names';
import type { AuthorisationData } from '@/common/composition/useLoginData';
import useLoginData, {
  activeFeature,
  FeatureFlagId,
} from '@/common/composition/useLoginData';
import components from '@/router/router-components';
import type { ZvdZRouteLocationNormalized } from '@/plugins/router';
import TriageType from '@/areas/triage/types';
import Deprecated from '@/router/Deprecated.vue';
import { UitDienstVersion } from '@/areas/rapporten/types';

const getComponent = (name: RouteName): Component => {
  const component = components.get(name);
  if (!component) {
    throw new Error(`No component found for route ${name}`);
  }
  return component;
};

const { isLoggedIn } = useLoginData();

const normalizeRoute = (
  route: ZvdZRouteLocationNormalized,
): ZvdZRouteLocationNormalized => ({
  ...route,
  meta: {
    ...(route.meta || {}),
    mustBeLoggedIn: route.meta?.mustBeLoggedIn !== false,
    showNavigation: route.meta?.showNavigation !== false,
    needsDienstverbandId: route.meta?.needsDienstverbandId || false,
    isAuthorised: route.meta?.isAuthorised || (() => true),
  },
  children: route.children?.map(normalizeRoute) || [],
});

const routes: RouteRecordRaw[] = [
  {
    path: '/Activiteiten/Afspraken',
    name: RouteName.ActiviteitenAfspraken,
    component: getComponent(RouteName.ActiviteitenAfspraken),
    meta: {
      isAuthorised: (input: AuthorisationData) => input.isAfsprakenAllowed,
    },
  },
  {
    path: '/Activiteiten/ActiviteitenOverzicht',
    name: RouteName.ActiviteitenActiviteitenOverzicht,
    component: getComponent(RouteName.ActiviteitenActiviteitenOverzicht),
    meta: {
      isAuthorised: (input: AuthorisationData) => input.isActiviteitenAllowed,
    },
  },
  {
    path: '/Activiteiten/Documenten',
    name: RouteName.ActiviteitenDocumenten,
    component: getComponent(RouteName.ActiviteitenDocumenten),
    meta: {
      isAuthorised: (input: AuthorisationData) => input.isDocumentenAllowed,
    },
  },
  {
    path: '/Activiteiten/Overzicht',
    name: RouteName.ActiviteitenOverzicht,
    component: getComponent(RouteName.ActiviteitenOverzicht),
  },
  {
    path: '/Admin',
    name: RouteName.Admin,
    component: getComponent(RouteName.Admin),
    children: [
      {
        path: 'InloggenAls',
        name: RouteName.AdminUserTools,
        component: getComponent(RouteName.AdminUserTools),
        meta: {
          isAuthorised: (input: AuthorisationData) => input.isSuperAdminAllowed,
        },
      },
      {
        path: 'FeatureFlags',
        name: RouteName.AdminFeatureFlags,
        component: getComponent(RouteName.AdminFeatureFlags),
        meta: {
          isAuthorised: (input: AuthorisationData) => input.isSuperAdminAllowed,
        },
      },
      {
        path: 'Organisations',
        name: RouteName.AdminOrganisations,
        component: getComponent(RouteName.AdminOrganisations),
        meta: {
          isAuthorised: (input: AuthorisationData) => input.isSuperAdminAllowed,
        },
      },
      {
        path: 'NotificationAfterLogin',
        name: RouteName.AdminNotificationAfterLogin,
        component: getComponent(RouteName.AdminNotificationAfterLogin),
        meta: {
          isAuthorised: (input: AuthorisationData) => input.isSuperAdminAllowed,
        },
      },
      {
        path: 'Verwerkersovereenkomst',
        name: RouteName.AdminVerwerkersovereenkomst,
        component: getComponent(RouteName.AdminVerwerkersovereenkomst),
        meta: {
          isAuthorised: (input: AuthorisationData) => input.isSuperAdminAllowed,
        },
      },
      ...(import.meta.env.MODE === 'development'
        ? [
            {
              path: 'Toggles',
              name: RouteName.AdminDevelopmentToggles,
              component: getComponent(RouteName.AdminDevelopmentToggles),
            },
          ]
        : []),
    ],
  },
  {
    path: '/BeheerZvdZ/ToegangVerzoeken',
    name: RouteName.BeheerZvdZRequestAccess,
    component: getComponent(RouteName.BeheerZvdZRequestAccess),
    meta: {
      isAuthorised: (input: AuthorisationData) =>
        Boolean(activeFeature.get(FeatureFlagId.beheerZvdZToegangVerzoeken)) &&
        (input.isMagMeekijkenRapportenVerzoekenAllowed ||
          input.isMagMeekijkenTotaalVerzoekenAllowed),
    },
  },
  {
    path: '/Contact',
    name: RouteName.ContactIndex,
    component: getComponent(RouteName.ContactIndex),
    meta: {
      needsDienstverbandId: true,
    },
  },
  {
    path: '/Contracten',
    name: RouteName.ContractenIndex,
    component: getComponent(RouteName.ContractenIndex),
    meta: {
      // Route beschikbaar / tegel nog zichtbaar voor Planningsagendaklanten
      isAuthorised: (input: AuthorisationData) => input.isContractenAllowed,
    },
  },
  {
    path: '/Facturen',
    name: RouteName.FacturenIndex,
    component: getComponent(RouteName.FacturenIndex),
    meta: {
      // Route beschikbaar / tegel nog zichtbaar voor Planningsagendaklanten
      isAuthorised: (input: AuthorisationData) => input.isFacturenAllowed,
    },
  },
  {
    path: '/GebruikersBeheer',
    alias: '/Gebruikersbeheer',
    name: RouteName.GebruikersBeheer,
    component: getComponent(RouteName.GebruikersBeheer),
    children: [
      {
        path: 'Gebruiker',
        name: RouteName.GebruikersBeheerGebruiker,
        component: getComponent(RouteName.GebruikersBeheerGebruiker),
        meta: {
          isAuthorised: (input: AuthorisationData) =>
            input.isGebruikersbeheerAllowed,
        },
      },
      {
        path: 'Gebruikers',
        alias: '',
        name: RouteName.GebruikersBeheerGebruikers,
        component: getComponent(RouteName.GebruikersBeheerGebruikers),
        meta: {
          isAuthorised: (input: AuthorisationData) =>
            input.isGebruikersbeheerAllowed,
        },
      },
      {
        path: 'VervangingRegelen',
        name: RouteName.GebruikersBeheerVervangingRegelen,
        component: getComponent(RouteName.GebruikersBeheerVervangingRegelen),
        meta: {
          isAuthorised: (input: AuthorisationData) =>
            input.isGebruikersbeheerAllowed,
        },
      },
      {
        path: 'TwoFactor',
        name: RouteName.GebruikersBeheerTwoFactor,
        component: getComponent(RouteName.GebruikersBeheerTwoFactor),
        meta: {
          isAuthorised: (input: AuthorisationData) =>
            input.isGebruikersbeheerAllowed,
        },
      },
    ],
  },
  {
    path: '/Gebruikersvoorwaarden',
    name: RouteName.GebruikersvoorwaardenIndex,
    component: getComponent(RouteName.GebruikersvoorwaardenIndex),
  },
  {
    path: '/Home/NietGeautoriseerd',
    name: RouteName.HomeNietGeautoriseerd,
    component: getComponent(RouteName.HomeNietGeautoriseerd),
  },
  {
    path: '/',
    name: RouteName.HomeIndex,
    alias: '/Home',
    component: getComponent(RouteName.HomeIndex),
  },
  {
    path: '/Interventies',
    name: RouteName.InterventiesIndex,
    component: Deprecated,
  },
  {
    path: '/Login/SsoNietGeautoriseerd',
    name: RouteName.SsoNietGeautoriseerd,
    component: getComponent(RouteName.SsoNietGeautoriseerd),
    meta: {
      mustBeLoggedIn: false,
      showNavigation: false,
    },
  },
  {
    path: '/Login/GebruikersNaamWijzigen',
    name: RouteName.LoginGebruikersNaamWijzigen,
    component: getComponent(RouteName.LoginGebruikersNaamWijzigen),
    meta: {
      mustBeLoggedIn: false,
      showNavigation: false,
    },
  },
  {
    path: '/Login',
    name: RouteName.LoginIndex,
    component: getComponent(RouteName.LoginIndex),
    beforeEnter: (
      to: ZvdZRouteLocationNormalized,
      from: ZvdZRouteLocationNormalized,
      next: NavigationGuardNext,
    ) => {
      if (isLoggedIn.value) {
        next({ name: RouteName.HomeIndex });
      }
      return next();
    },
    meta: {
      mustBeLoggedIn: false,
      showNavigation: false,
    },
  },
  {
    path: '/MijnProfiel/BevestigContactgegevens',
    name: RouteName.MijnProfielBevestigContactgegevens,
    component: getComponent(RouteName.MijnProfielBevestigContactgegevens),
    meta: {
      showNavigation: false,
    },
  },
  {
    path: '/LoggedOut',
    name: RouteName.LoggedOut,
    component: getComponent(RouteName.LoggedOut),
    meta: {
      mustBeLoggedIn: false,
      showNavigation: false,
    },
  },
  {
    path: '/Login/LogOut',
    name: RouteName.Logout,
    component: getComponent(RouteName.LoginIndex),
    props: { logOut: true },
    meta: {
      mustBeLoggedIn: false,
      showNavigation: false,
    },
  },
  {
    path: '/Login/PasswordRecovery',
    name: RouteName.LoginPasswordRecovery,
    component: getComponent(RouteName.LoginPasswordRecovery),
    props: { mode: ActivationOrPasswordRecoveryMode.recovery },
    meta: {
      mustBeLoggedIn: false,
      showNavigation: false,
    },
  },
  {
    path: '/Login/Activatie',
    name: RouteName.LoginActivatie,
    component: getComponent(RouteName.LoginPasswordRecovery),
    props: { mode: ActivationOrPasswordRecoveryMode.activation },
    meta: {
      showNavigation: false,
      mustBeLoggedIn: false,
    },
  },
  {
    path: '/MijnProfiel',
    name: RouteName.MijnProfielIndex,
    component: getComponent(RouteName.MijnProfielIndex),
    children: [
      {
        path: 'MijnAccount',
        name: RouteName.MijnProfielMijnAccount,
        component: getComponent(RouteName.MijnProfielMijnAccount),
      },
      {
        path: 'MijnAutorisatie',
        name: RouteName.MijnProfielMijnAutorisatie,
        component: getComponent(RouteName.MijnProfielMijnAutorisatie),
        meta: {
          isAuthorised: (input: AuthorisationData) =>
            input.isMijnToegangAllowed,
        },
      },
      {
        path: 'MijnVervanging',
        name: RouteName.MijnProfielMijnVervanging,
        component: getComponent(RouteName.MijnProfielMijnVervanging),
        meta: {
          isAuthorised: (input: AuthorisationData) => input.isVervangingAllowed,
        },
      },
    ],
  },
  {
    path: '/OnlineStore',
    name: RouteName.OnlineStoreIndex,
    component: Deprecated,
    meta: {
      featureFlagId: FeatureFlagId.vueOnlineStoreIndex,
      isAuthorised: (input: AuthorisationData) =>
        input.isOnlineStoreAllowed &&
        activeFeature.get(FeatureFlagId.vueOnlineStoreIndex),
    },
  },
  {
    path: '/Prolongatie',
    name: RouteName.ProlongatieIndex,
    component: getComponent(RouteName.ProlongatieIndex),
    meta: { mustBeLoggedIn: false },
  },
  {
    path: '/Videogesprek/:id',
    alias: ['/v/:id'],
    name: RouteName.VideogesprekIndex,
    component: getComponent(RouteName.VideogesprekIndex),
    meta: {
      mustBeLoggedIn: false,
    },
  },
  {
    path: '/Rapporten/BasisDashboard',
    name: RouteName.RapportenBasisDashboard,
    component: getComponent(RouteName.RapportenBasisDashboard),
    meta: {
      isAuthorised: (input: AuthorisationData) =>
        input.isRapportageAllowed &&
        !input.isATP &&
        !input.isAdministratiekantoor,
    },
  },
  {
    path: '/Rapporten/UitgebreidDashboard',
    name: RouteName.RapportenUitgebreidDashboard,
    component: getComponent(RouteName.RapportenUitgebreidDashboard),
    meta: {
      isAuthorised: (input: AuthorisationData) =>
        input.isRapportageAllowed &&
        !input.isATP &&
        !input.isAdministratiekantoor,
    },
  },
  {
    path: '/Rapporten/Basis',
    name: RouteName.RapportenBasis,
    component: getComponent(RouteName.RapportenBasis),
    meta: {
      isAuthorised: (input: AuthorisationData) =>
        input.isRapportageAllowed &&
        !input.isATP &&
        !input.isAdministratiekantoor,
    },
  },
  {
    path: '/Rapporten/Verzuimhistorie',
    name: RouteName.RapportenVerzuimhistorie,
    component: getComponent(RouteName.RapportenVerzuimhistorie),
  },
  {
    path: '/Rapporten/WgaStatus',
    name: RouteName.RapportenWgaStatus,
    component: getComponent(RouteName.RapportenWgaStatus),
  },
  {
    path: '/Rapporten/WAOStatus',
    name: RouteName.RapportenWAOStatus,
    component: getComponent(RouteName.RapportenWAOStatus),
  },
  {
    path: '/Rapporten/NoRiskpolis',
    name: RouteName.RapportenNoRiskpolis,
    component: getComponent(RouteName.RapportenNoRiskpolis),
  },
  {
    path: '/Rapporten/ZiekUitDienst',
    name: RouteName.RapportenZiekUitDienst,
    component: getComponent(RouteName.RapportenUitDienst),
    props: { version: UitDienstVersion.verzuimend },
  },
  {
    path: '/Rapporten/UitDienst',
    name: RouteName.RapportenUitDienst,
    props: { version: UitDienstVersion.default },
    component: getComponent(RouteName.RapportenUitDienst),
  },
  {
    path: '/Rapporten/Export',
    name: RouteName.RapportenExport,
    component: getComponent(RouteName.RapportenExport),
    meta: {
      isAuthorised: (input: AuthorisationData) =>
        input.isRapportageAllowed &&
        !input.isATP &&
        !input.isAdministratiekantoor,
    },
  },
  {
    path: '/Registratie/Aanmelden',
    name: RouteName.RegistratieAanmelden,
    component: getComponent(RouteName.RegistratieAanmelden),
    meta: {
      mustBeLoggedIn: false,
    },
  },
  {
    path: '/Schadelastbeheersing/Schadelastbeheersing',
    name: RouteName.SchadelastbeheersingSchadelastbeheersing,
    component: getComponent(RouteName.SchadelastbeheersingSchadelastbeheersing),
    meta: {
      isAuthorised: (input: AuthorisationData) =>
        input.isSchadelastbeheersingAllowed,
    },
  },
  {
    path: '/Settings/Informatie',
    name: RouteName.SettingsInformatie,
    component: Deprecated,
  },
  {
    path: '/Settings/Sjablonen',
    name: RouteName.SettingsSjablonen,
    component: Deprecated,
    meta: {
      featureFlagId: FeatureFlagId.vueSettingsSjablonen,
      isAuthorised: (input: AuthorisationData) =>
        input.isAdmin && input.isOproepbriefAdmin,
    },
  },
  {
    path: '/Settings',
    name: RouteName.Settings,
    component: getComponent(RouteName.Settings),
    children: [
      {
        path: 'Modules',
        name: RouteName.SettingsModules,
        component: getComponent(RouteName.SettingsModules),
        meta: {
          isAuthorised: (input: AuthorisationData) =>
            input.isInstellingenAllowed,
        },
      },
      {
        path: 'ExtraRechten',
        name: RouteName.SettingsAssurantie,
        component: getComponent(RouteName.SettingsAssurantie),
        meta: {
          isAuthorised: (input: AuthorisationData) =>
            input.isInstellingenAllowed,
        },
      },
      {
        path: 'VerzuimProtocol',
        name: RouteName.SettingsVerzuimProtocol,
        component: getComponent(RouteName.SettingsVerzuimProtocol),
        meta: {
          isAuthorised: (input: AuthorisationData) =>
            input.isHeeftWerkgevergroepOrWerkgever,
        },
      },
      {
        path: 'OrganisatieProtocol',
        name: RouteName.SettingsOrganisatieProtocol,
        component: getComponent(RouteName.SettingsOrganisatieProtocol),
        meta: {
          isAuthorised: (input: AuthorisationData) =>
            input.isOrganisatieProtocolAllowed,
        },
      },
      {
        path: 'Correspondentie',
        name: RouteName.SettingsCorrespondentie,
        component: getComponent(RouteName.SettingsCorrespondentie),
        meta: {
          isAuthorised: (input: AuthorisationData) =>
            input.isGebruikersbeheerAllowed,
        },
      },
    ],
  },
  {
    path: '/Triage/WG/:triageCode',
    name: RouteName.TriageWG,
    component: getComponent(RouteName.TriageWG),
    props: { triageType: TriageType.WG },
    meta: { mustBeLoggedIn: false },
  },
  {
    path: '/Triage/WN/:triageCode',
    name: RouteName.TriageWN,
    component: getComponent(RouteName.TriageWN),
    props: { triageType: TriageType.WN },
    meta: { mustBeLoggedIn: false },
  },
  {
    path: '/UwGegevens',
    name: RouteName.UwGegevensIndex,
    component: getComponent(RouteName.UwGegevensIndex),
    meta: {
      isAuthorised: (input: AuthorisationData) => input.isUwGegevensAllowed,
    },
  },
  {
    path: '/Verwerkersovereenkomst',
    name: RouteName.VerwerkersovereenkomstIndex,
    component: getComponent(RouteName.VerwerkersovereenkomstIndex),
    meta: {
      // Route beschikbaar / tegel nog zichtbaar voor Planningsagendaklanten
      isAuthorised: (input: AuthorisationData) =>
        input.isVerwerkersovereenkomstAkkoordInzienAllowed,
    },
  },
  {
    path: '/WerknemerDossier/VRDossier',
    name: RouteName.WerknemerDossierVRDossier,
    component: getComponent(RouteName.WerknemerDossierVRDossier),
    meta: {
      needsDienstverbandId: true,
    },
  },
  {
    path: '/WerknemerDossier/WgaDossier',
    name: RouteName.WerknemerDossierWgaDossier,
    component: getComponent(RouteName.WerknemerDossierWgaDossier),
    meta: {
      needsDienstverbandId: true,
    },
  },
  {
    path: '/Werknemer',
    name: RouteName.WerknemerIndex,
    component: getComponent(RouteName.WerknemerIndex),
    meta: {
      isAuthorised: (input: AuthorisationData) => input.isWerknemersAllowed,
    },
  },
  {
    path: '/Werknemer/Correspondentie',
    name: RouteName.WerknemerCorrespondentie,
    component: getComponent(RouteName.WerknemerCorrespondentie),
    meta: {
      needsDienstverbandId: true,
    },
  },
  {
    path: '/Werknemer/MijnTaken',
    name: RouteName.WerknemerMijnTaken,
    component: getComponent(RouteName.ActiviteitenActiviteitenOverzicht),
    meta: {
      needsDienstverbandId: true,
    },
  },
  {
    path: '/Werknemer/Afspraken',
    name: RouteName.WerknemerAfspraken,
    component: getComponent(RouteName.WerknemerAfspraken),
  },
  {
    path: '/Werknemer/Werknemer',
    name: RouteName.WerknemerWerknemer,
    component: getComponent(RouteName.WerknemerWerknemer),
    meta: {
      needsDienstverbandId: true,
    },
  },
  {
    path: '/Werknemer/Persoonsgegevens',
    name: RouteName.WerknemerPersoonsgegevens,
    component: getComponent(RouteName.WerknemerPersoonsgegevens),
  },
  {
    path: '/Werknemer/InformatieDuurzameInzetbaarheid',
    name: RouteName.WerknemerWerknemersgegevens,
    component: getComponent(RouteName.WerknemerWerknemersgegevens),
  },
  {
    path: '/Werknemer/Dienstverband',
    name: RouteName.WerknemerDienstverband,
    component: getComponent(RouteName.WerknemerDienstverband),
  },
  {
    path: '/Werknemer/VerzuimHistorie',
    name: RouteName.WerknemerVerzuimHistorie,
    component: getComponent(RouteName.WerknemerVerzuimHistorie),
  },
  {
    path: '/Werknemer/Verzuimkalender',
    name: RouteName.WerknemerVerzuimkalender,
    component: getComponent(RouteName.WerknemerVerzuimkalender),
    meta: {
      needsDienstverbandId: true,
    },
  },
  {
    path: '/Werknemer/Dossiertoegang',
    name: RouteName.WerknemerDossiertoegang,
    component: getComponent(RouteName.WerknemerDossiertoegang),
    meta: {
      needsDienstverbandId: true,
    },
  },
  {
    path: '/WrongPortal',
    name: RouteName.WrongPortal,
    component: getComponent(RouteName.WrongPortal),
    meta: {
      mustBeLoggedIn: false,
    },
  },
  {
    path: '/Zelfservice',
    name: RouteName.ZelfserviceIndex,
    component: getComponent(RouteName.ZelfserviceIndex),
    meta: {
      // Route beschikbaar / tegel nog zichtbaar voor Planningsagendaklanten
      isAuthorised: (input: AuthorisationData) => input.isZelfserviceAllowed,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: RouteName.CatchAll,
    component: getComponent(RouteName.PaginaNietGevonden),
    meta: { mustBeLoggedIn: false },
  },
].map(normalizeRoute as (route: unknown) => RouteRecordRaw);

const getDuplicatesRouteNames = (arr: RouteRecordRaw[]) => {
  const keys = arr.map(item => item.name);
  return keys.filter(key => keys.indexOf(key) !== keys.lastIndexOf(key));
};

if (import.meta.env.MODE === 'development') {
  const duplicates = getDuplicatesRouteNames(routes);
  if (duplicates.length > 0) {
    throw new Error(`Route host names are not unique!: ${duplicates}`);
  }
}

export default routes;
